import React, { useState } from 'react';
import axios from 'axios';
import { History, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

import API from '../utils/api';

const FIELD_LABELS = {
  name: 'Name', phone: 'Phone', email: 'Email', dob: 'Date of Birth',
  department: 'Department', role: 'Role', employment_type: 'Type',
  reporting_manager: 'Manager', status: 'Status', base_salary: 'Base Salary',
  hra: 'HRA', allowances: 'Allowances', bonus: 'Bonus', deductions: 'Deductions',
  total_salary: 'Total Salary', review_date: 'Review Date', aadhar_verified: 'Aadhar Verified',
  industry: 'Industry', contact_name: 'Contact', shoot_date: 'Shoot Date',
  location: 'Location', shoot_type: 'Shoot Type', team: 'Team',
  reporting_time: 'Reporting Time', brief: 'Brief', priority: 'Priority',
  task_type: 'Task Type', assigned_to: 'Assigned To', client_id: 'Client',
  raw_file_link: 'Raw File', script: 'Script', final_drive_link: 'Final Link',
  special_production: 'Special', category: 'Category', brand: 'Brand', model: 'Model',
  followers_count: 'Followers', engagement_level: 'Engagement', dominant_platform: 'Platform',
  price_per_reel: 'Price/Reel', price_per_post: 'Price/Post', price_per_story: 'Price/Story',
  price_per_shoot: 'Price/Shoot', scope_type: 'Scope Type', scope_name: 'Scope',
  amount: 'Amount', tax_amount: 'Tax', total_amount: 'Total', invoice_date: 'Invoice Date',
  due_date: 'Due Date', raw_footage_link: 'Raw Footage', client_notes: 'Client Notes',
  internal_notes: 'Internal Notes', start_time: 'Start Time', end_time: 'End Time',
  exit_date: 'Exit Date', review_date: 'Review Date',
};

function formatFieldName(field) {
  return FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatTimestamp(isoStr) {
  return new Date(isoStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function ChangeHistoryButton({ resource, recordId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOpen = async (e) => {
    e.stopPropagation();
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/change-history/${resource}/${recordId}`, { withCredentials: true });
      setHistory(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="p-1 rounded hover:bg-white/10 transition-colors group"
        title="View change history"
        data-testid={`history-btn-${recordId}`}
      >
        <History size={14} className="text-slate-600 group-hover:text-indigo-400 transition-colors" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="change-history-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-lg flex items-center gap-2">
              <History size={18} className="text-indigo-400" />
              Change History
            </DialogTitle>
            <p className="text-xs text-slate-500 font-mono">{recordId}</p>
          </DialogHeader>

          <div className="py-2">
            {loading ? (
              <div className="text-center py-8 text-slate-500 text-sm">Loading...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <History size={32} className="text-slate-700 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No changes recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((entry, idx) => (
                  <div key={idx} className="relative pl-5 pb-3 border-l border-white/10 last:border-l-transparent" data-testid={`history-entry-${idx}`}>
                    <div className="absolute left-[-4px] top-1 w-2 h-2 rounded-full bg-indigo-500" />
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-white">{entry.changed_by}</span>
                      <span className="text-[10px] text-slate-500" title={formatTimestamp(entry.timestamp)}>
                        {timeAgo(entry.timestamp)}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {entry.changes.map((change, ci) => (
                        <div key={ci} className="bg-white/[0.03] rounded px-2.5 py-1.5 text-xs">
                          <span className="text-slate-400">{formatFieldName(change.field)}</span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {change.old_value ? (
                              <>
                                <span className="text-rose-400/70 line-through truncate max-w-[140px]" title={change.old_value}>{change.old_value}</span>
                                <span className="text-slate-600">→</span>
                                <span className="text-emerald-400 truncate max-w-[140px]" title={change.new_value}>{change.new_value}</span>
                              </>
                            ) : (
                              <span className="text-emerald-400 truncate max-w-[200px]" title={change.new_value}>Set to: {change.new_value}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
